import api from './client';

const get = async (path) => (await api.get(path)).data?.data;
const post = async (path, body = {}) => (await api.post(path, body)).data?.data;
const patch = async (path, body = {}) => (await api.patch(path, body)).data?.data;
const put = async (path, body = {}) => (await api.put(path, body)).data?.data;
const del = async (path, body = null) =>
  (await api.delete(path, body ? { data: body } : undefined)).data?.data;

/**
 * 📮 Enquiries — the record behind the contact forms.
 *
 * `submitEnquiry` is deliberately callable with no session: the person filling
 * in a sales, support, partner or careers form does not have an account, which
 * is the whole reason the enquiry exists.
 */
export const enquiries = {
  submit: (body) => post('/communications/comms/enquiries', body),
  list: ({ kind = '', status = '' } = {}) => {
    const qs = new URLSearchParams();
    if (kind) qs.set('kind', kind);
    if (status) qs.set('status', status);
    const q = qs.toString();
    return get(`/admin/communications/enquiries${q ? `?${q}` : ''}`);
  },
  update: (id, body) => patch(`/admin/communications/enquiries/${id}`, body)
};

export const communications = {
  sendEmail: (body) => post('/communications/send/email', body),
  sendSms: (body) => post('/communications/send/sms', body),
  sendPush: (body) => post('/communications/send/push', body),
  batchSend: (body) => post('/communications/batch-send', body),
  deliveryStatus: (messageId) => get(`/communications/delivery-status/${messageId}`),

  createThread: (body) => post('/communications/chat/threads', body),
  // Optional `label` scopes the rail to threads carrying that tag (?label=).
  listThreads: (label) => {
    const qs = label ? `?label=${encodeURIComponent(label)}` : '';
    return get(`/communications/chat/threads${qs}`);
  },
  // Paginated thread fetch. `getThread(id)` still returns the latest page
  // ({ ...thread, messages, hasMore, cursor }); pass { before } (the ISO
  // cursor of the oldest loaded message) to page backwards through history,
  // and { limit } to size the page.
  getThread: (threadId, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.before) params.set('before', String(opts.before));
    const qs = params.toString();
    return get(`/communications/chat/threads/${threadId}${qs ? `?${qs}` : ''}`);
  },
  // Global (or thread-scoped) message search across the caller's threads.
  // Returns { query, results: [ { ...message, threadId } ] }. Min 2 chars.
  searchChat: (q, threadId) => {
    const params = new URLSearchParams({ q: String(q || '') });
    if (threadId) params.set('threadId', String(threadId));
    return get(`/communications/chat/search?${params.toString()}`);
  },
  chatUnread: () => get('/communications/chat/unread'),
  markThreadRead: (threadId) => patch(`/communications/chat/threads/${threadId}/read`, {}),
  markThreadUnread: (threadId) => patch(`/communications/chat/threads/${threadId}/unread`, {}),
  setThreadMuted: (threadId, muted) => patch(`/communications/chat/threads/${threadId}/mute`, { muted: !!muted }),
  setThreadPinned: (threadId, pinned) => patch(`/communications/chat/threads/${threadId}/pin`, { pinned: !!pinned }),
  archiveThread: (threadId) => patch(`/communications/chat/threads/${threadId}/archive`, {}),
  // Group management. addThreadParticipants / removeThreadParticipant both
  // return the updated thread ({ participants, ... }). Any participant may
  // add; removing others needs admin or the thread creator; anyone may
  // remove themselves (leave). The backend enforces this and 403s otherwise.
  addThreadParticipants: (threadId, userIds) =>
    post(`/communications/chat/threads/${threadId}/participants`, { userIds }),
  removeThreadParticipant: (threadId, userId) =>
    del(`/communications/chat/threads/${threadId}/participants/${userId}`),
  // Accepts { internal: true, visibleTo: [userId,…] } for an internal note
  // (omit both for a normal, customer-visible message).
  sendThreadMessage: (threadId, body) => post(`/communications/chat/threads/${threadId}/messages`, body),
  addThreadAttachment: (threadId, body) => post(`/communications/chat/threads/${threadId}/attachments`, body),
  markMessageRead: (messageId) => patch(`/communications/chat/messages/${messageId}/read`, {}),
  deleteMessage: (messageId, body) => del(`/communications/chat/messages/${messageId}`, body),
  // Rich messaging. editMessage returns the updated message (has editedAt);
  // reactToMessage toggles the caller's reaction and returns the updated
  // message (has the merged reactions map).
  editMessage: (messageId, content) => patch(`/communications/chat/messages/${messageId}`, { content }),
  reactToMessage: (messageId, emoji) => post(`/communications/chat/messages/${messageId}/reactions`, { emoji }),

  // ── Thread depth (Phase 2) ────────────────────────────────────────────
  // Hand a thread off from one assignee to another. Admin/operator or the
  // thread creator only (backend-enforced; a stray attempt 403s).
  reassignThread: (threadId, fromUserId, toUserId) =>
    post(`/communications/chat/threads/${threadId}/reassign`, { fromUserId, toUserId }),
  // Add and/or remove free-text labels on a thread; returns the updated thread.
  patchThreadLabels: (threadId, add = [], remove = []) =>
    patch(`/communications/chat/threads/${threadId}/labels`, { add, remove }),
  // Set (or clear) what the conversation is about. `ref` = { kind, id, label, url }.
  setThreadContext: (threadId, ref) =>
    patch(`/communications/chat/threads/${threadId}/context`, ref),
  // Customer satisfaction rating (1–5) with an optional free-text comment.
  submitThreadCsat: (threadId, rating, comment) =>
    post(`/communications/chat/threads/${threadId}/csat`, { rating, comment }),

  listTemplates: () => get('/communications/templates'),
  createTemplate: (body) => post('/communications/templates', body),
  updateTemplate: (templateId, body) => put(`/communications/templates/${templateId}`, body),
  testTemplate: (body) => post('/communications/templates/test', body),
  templateVersions: (templateId) => get(`/communications/templates/versions${templateId ? `?templateId=${encodeURIComponent(templateId)}` : ''}`),

  myPreferences: () => get('/communications/me/preferences'),
  patchMyPreferences: (body) => patch('/communications/me/preferences', body),
  unsubscribe: (body) => post('/communications/unsubscribe', body),
  myBlacklist: () => get('/communications/me/blacklist'),
  addBlacklist: (userId) => post(`/communications/me/blacklist/${userId}`, {}),

  // Moderation: every chat thread on the platform (admin-gated).
  adminListThreads: () => get('/admin/communications/chat/threads'),

  // Staff stepping into / out of a live conversation. Not on the /admin
  // prefix: the service gates these itself, and they act on a normal thread
  // rather than on the moderation feed.
  intervene: (threadId, reason) => post(`/communications/chat/threads/${threadId}/intervene`, { reason }),
  endIntervention: (threadId, note) => post(`/communications/chat/threads/${threadId}/intervene/end`, { note }),

  adminBroadcast: (body) => post('/admin/communications/broadcast', body),
  adminBroadcastHistory: () => get('/admin/communications/broadcasts/history'),
  adminCancelBroadcast: (id) => post(`/admin/communications/broadcasts/${id}/cancel`, {}),

  listProviders: () => get('/communications/providers'),
  registerProvider: (body) => post('/communications/providers', body),
  setProviderActive: (id, body) => patch(`/communications/providers/${id}/active`, body),
  providersHealth: () => get('/communications/providers/health'),
  providersFailover: (body) => post('/communications/providers/failover', body),

  // Real, encrypted, DB-backed credentials per provider (smtp,
  // mailgun, sendgrid, twilio). Never round-trips real secret values —
  // only masks/configuredFields.
  credentialStatus: () => get('/communications/providers/credentials/status'),
  upsertProviderCredentials: (provider, body) => patch(`/communications/providers/${provider}/credentials`, body),
  testProviderConnection: (provider) => post(`/communications/providers/${provider}/test`, {}),
  clearProviderCredentials: (provider) => del(`/communications/providers/${provider}/credentials`),

  filterProfanity: (body) => post('/communications/filter/profanity', body),
  filterPii: (body) => post('/communications/filter/pii', body),
  adminLogs: (limit = 100) => get(`/admin/communications/logs?limit=${limit}`),
  adminStats: () => get('/admin/communications/stats'),

  webhookSendgrid: (body) => post('/communications/webhooks/sendgrid', body),
  webhookTwilio: (body) => post('/communications/webhooks/twilio', body)
};

